import { z } from 'zod';
import {
  productFeatureSchema,
  productListsInputSchema,
  productTextListSchema,
} from './product-texts.js';

/**
 * The catalog: Avrahami's master catalog (filled only by the catalog feed ingest) and each
 * tenant's own catalog (docs/CATALOG.md). Enum values are shared with the database enums; a test
 * checks that they match exactly.
 */

/** Whether a master row still exists at the source. Discontinued rows are kept, never deleted. */
export const CATALOG_SOURCE_STATUSES = ['ACTIVE', 'DISCONTINUED'] as const;
export const catalogSourceStatusSchema = z.enum(CATALOG_SOURCE_STATUSES);
export type CatalogSourceStatus = z.infer<typeof catalogSourceStatusSchema>;

export const CATALOG_INGEST_MODES = ['FULL', 'INCREMENTAL'] as const;
export const catalogIngestModeSchema = z.enum(CATALOG_INGEST_MODES);
export type CatalogIngestMode = z.infer<typeof catalogIngestModeSchema>;

export const CATALOG_INGEST_STATUSES = ['RUNNING', 'SUCCEEDED', 'FAILED'] as const;
export const catalogIngestStatusSchema = z.enum(CATALOG_INGEST_STATUSES);
export type CatalogIngestStatus = z.infer<typeof catalogIngestStatusSchema>;

/** Where a tenant product came from. */
export const PRODUCT_ORIGINS = ['MASTER_CATALOG', 'TENANT_CUSTOM'] as const;
export const productOriginSchema = z.enum(PRODUCT_ORIGINS);
export type ProductOrigin = z.infer<typeof productOriginSchema>;

/** ARCHIVED is the tenant's "delete": the row stays, so sync never offers it as new again. */
export const PRODUCT_STATUSES = ['DRAFT', 'ACTIVE', 'HIDDEN', 'ARCHIVED'] as const;
export const productStatusSchema = z.enum(PRODUCT_STATUSES);
export type ProductStatus = z.infer<typeof productStatusSchema>;

/** The only column product sync may ever change on an existing tenant product. */
export const PRODUCT_SOURCE_STATES = ['IN_SYNC', 'SOURCE_DISCONTINUED'] as const;
export const productSourceStateSchema = z.enum(PRODUCT_SOURCE_STATES);
export type ProductSourceState = z.infer<typeof productSourceStateSchema>;

/**
 * Marketing copy lifecycle. SOURCE_PENDING means no copy of the tenant's own yet: Avrahami's text
 * is never copied to a tenant, and such a product cannot be published (docs/AI_CONTENT.md).
 */
export const CONTENT_STATES = [
  'SOURCE_PENDING',
  'AI_GENERATED',
  'APPROVED',
  'TENANT_EDITED',
] as const;
export const contentStateSchema = z.enum(CONTENT_STATES);
export type ContentState = z.infer<typeof contentStateSchema>;

export const CURRENCIES = ['ILS'] as const;
export const currencySchema = z.enum(CURRENCIES);
export type Currency = z.infer<typeof currencySchema>;

/**
 * Why a product cannot be ACTIVE (shown on the storefront) yet. A price never blocks it: a product
 * without a price of its own costs 0 and is shown all the same (shoppers see no price on it).
 */
export const PUBLISH_BLOCKERS = ['ARCHIVED', 'NO_COPY'] as const;
export const publishBlockerSchema = z.enum(PUBLISH_BLOCKERS);
export type PublishBlocker = z.infer<typeof publishBlockerSchema>;

/**
 * Why a product the store holds is not shown to shoppers (it is DRAFT or HIDDEN): what it still
 * lacks, or READY when it lacks nothing and only waits for the store to switch it on.
 */
/**
 * Why shoppers do not see an ACTIVE product (adr/0046): the original site does not show the item
 * to the store's customer (no price for it there, out of stock, ...), or every category it is
 * placed in is hidden, itself or above it.
 */
export const SHOPPER_HIDDEN_REASONS = ['NOT_ON_SITE', 'CATEGORY_HIDDEN'] as const;
export const shopperHiddenReasonSchema = z.enum(SHOPPER_HIDDEN_REASONS);
export type ShopperHiddenReason = z.infer<typeof shopperHiddenReasonSchema>;

export const NOT_SHOWN_REASONS = ['NO_COPY', 'READY'] as const;
export const notShownReasonSchema = z.enum(NOT_SHOWN_REASONS);
export type NotShownReason = z.infer<typeof notShownReasonSchema>;

/** Money in minor units (agorot). Up to 10 million shekels. */
export const amountMinorSchema = z.number().int().min(0).max(1_000_000_000);

/** One row of a product's technical specification ("Width" → "60 cm"). */
export const specRowSchema = z.object({
  label: z.string().trim().min(1).max(120),
  value: z.string().trim().min(1).max(500),
});
export type SpecRow = z.infer<typeof specRowSchema>;
export const specListSchema = z.array(specRowSchema).max(100);

const optionalText = (max: number) => z.string().trim().min(1).max(max).nullable();

// ---------------------------------------------------------------------------------------------
// Tenant catalog (tenant admin surface)
// ---------------------------------------------------------------------------------------------

/**
 * Technical facts. For catalog products they are copied from the master catalog and are
 * read-only (`locked`); the tenant may add its own rows in `extraSpecs`.
 */
export const productFactsSchema = z.object({
  name: z.string(),
  sku: z.string(),
  barcode: z.string().nullable(),
  model: z.string().nullable(),
  manufacturer: z.string().nullable(),
  specs: specListSchema,
  extraSpecs: specListSchema,
  locked: z.boolean(),
});
export type ProductFacts = z.infer<typeof productFactsSchema>;

export const productContentSchema = z.object({
  title: z.string().nullable(),
  description: z.string().nullable(),
  seoTitle: z.string().nullable(),
  seoDescription: z.string().nullable(),
  state: contentStateSchema,
  /**
   * The strengths, "ideal for" lines and care instructions of the product's page (adr/0048,
   * adr/0054): null until the engine writes them (a catalog product), or the store fills them.
   */
  features: z.array(productFeatureSchema).nullable(),
  idealFor: z.array(z.string()).nullable(),
  care: z.array(z.string()).nullable(),
  /** The lists the store edited by hand (adr/0055): no rewrite of the engine replaces them. */
  editedLists: z.array(productTextListSchema),
  /** The engine is writing the product's copy or lists now (a job of the product waits or runs). */
  writing: z.boolean(),
});
export type ProductContent = z.infer<typeof productContentSchema>;

/**
 * The profit a product's sale price starts with, over its cost, until the store sets a price of
 * its own: 100 means the sale price is twice the cost.
 */
export const DEFAULT_MARKUP_PERCENT = 100;

/**
 * `amountMinor` is the sale price the store sets. `costMinor` is what the product costs the
 * store (VAT included, as the sale price is): on a catalog product the price on the original
 * site, kept by the platform; on the store's own product whatever the store typed. The profit
 * is the difference. The cost is for the store only and never reaches a shopper.
 */
export const productPriceSchema = z.object({
  amountMinor: amountMinorSchema.nullable(),
  compareAtMinor: amountMinorSchema.nullable(),
  costMinor: amountMinorSchema.nullable(),
  currency: currencySchema,
});
export type ProductPrice = z.infer<typeof productPriceSchema>;

/** `tracked: false` means "available to order", with no quantity kept (IMPLEMENTATION_PLAN Q7). */
export const productInventorySchema = z.object({
  tracked: z.boolean(),
  quantity: z.number().int().min(0),
  lowStockThreshold: z.number().int().min(0).nullable(),
});
export type ProductInventory = z.infer<typeof productInventorySchema>;

export const productMediaSchema = z.object({
  id: z.uuid(),
  position: z.number().int().min(0),
  altText: z.string().nullable(),
  contentType: z.string(),
  /** Content address of the image in platform storage. */
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  /** The image's file name under `/v1/tenant/media/<file>` (and `/media/<file>` on the store). */
  file: z.string(),
  /** Copied from the catalog, or uploaded by the store. */
  origin: z.enum(['CATALOG', 'UPLOAD']),
});
export type ProductMedia = z.infer<typeof productMediaSchema>;

/**
 * Where the manufacturer logo a product shows comes from (adr/0041): the catalog's, which follows
 * Avrahami's own store (none when it shows none); one of the store's uploads; or none, as the
 * store chose. Only catalog products have a catalog logo.
 */
export const PRODUCT_LOGO_SOURCES = ['CATALOG', 'UPLOAD', 'NONE'] as const;
export const productLogoSourceSchema = z.enum(PRODUCT_LOGO_SOURCES);
export type ProductLogoSource = z.infer<typeof productLogoSourceSchema>;

export const productLogoSchema = z.object({
  source: productLogoSourceSchema,
  /** The logo's file name under `/v1/tenant/media/<file>`; null when the product shows none. */
  file: z.string().nullable(),
});
export type ProductLogo = z.infer<typeof productLogoSchema>;

export const productSummarySchema = z.object({
  id: z.uuid(),
  origin: productOriginSchema,
  status: productStatusSchema,
  sourceState: productSourceStateSchema,
  name: z.string(),
  sku: z.string(),
  title: z.string().nullable(),
  contentState: contentStateSchema,
  amountMinor: amountMinorSchema.nullable(),
  compareAtMinor: amountMinorSchema.nullable(),
  costMinor: amountMinorSchema.nullable(),
  currency: currencySchema,
  stockTracked: z.boolean(),
  quantity: z.number().int(),
  /** Why shoppers do not see the product even when it is ACTIVE; null when nothing hides it. */
  hiddenFromShoppers: shopperHiddenReasonSchema.nullable(),
  /** Empty when the product may be ACTIVE. */
  publishBlockers: z.array(publishBlockerSchema),
  version: z.number().int(),
  updatedAt: z.iso.datetime(),
});
export type ProductSummary = z.infer<typeof productSummarySchema>;

export const productDetailSchema = z.object({
  id: z.uuid(),
  origin: productOriginSchema,
  status: productStatusSchema,
  sourceState: productSourceStateSchema,
  version: z.number().int(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  facts: productFactsSchema,
  content: productContentSchema,
  price: productPriceSchema,
  inventory: productInventorySchema,
  categoryIds: z.array(z.uuid()),
  media: z.array(productMediaSchema),
  /** The manufacturer logo shown on the product's card and page. */
  logo: productLogoSchema,
  /** Why shoppers do not see the product even when it is ACTIVE; null when nothing hides it. */
  hiddenFromShoppers: shopperHiddenReasonSchema.nullable(),
  /** Empty when the product may be ACTIVE. */
  publishBlockers: z.array(publishBlockerSchema),
});
export type ProductDetail = z.infer<typeof productDetailSchema>;

/** LOW: tracked and at or below its low-stock threshold (but not out); OUT: tracked and at 0. */
export const STOCK_FILTERS = ['LOW', 'OUT'] as const;
export const stockFilterSchema = z.enum(STOCK_FILTERS);
export type StockFilter = z.infer<typeof stockFilterSchema>;

/**
 * Product lists are shown under their categories (category, then its sub-categories, then the
 * products). A list answers which categories its products are in with `productCategoryGroups`,
 * and takes `categoryId` to list the products of one of them.
 */

/** The `categoryId` of products that are placed in no category. */
export const UNCATEGORIZED = 'none';

/** One category, or `none` for products placed in no category. */
export const categoryFilterSchema = z.union([z.uuid(), z.literal(UNCATEGORIZED)]);
export type CategoryFilter = z.infer<typeof categoryFilterSchema>;

export const productCategoryGroupSchema = z.object({
  id: z.uuid(),
  parentId: z.uuid().nullable(),
  name: z.string(),
  /** Products of the list placed directly in this category (not in the ones below it). */
  productCount: z.number().int().min(0),
});
export type ProductCategoryGroup = z.infer<typeof productCategoryGroupSchema>;

/** Every category in display order, and how many products of the list are in no category. */
export const productCategoryGroupsSchema = z.object({
  items: z.array(productCategoryGroupSchema),
  uncategorized: z.number().int().min(0),
});
export type ProductCategoryGroups = z.infer<typeof productCategoryGroupsSchema>;

export const productListQuerySchema = z.object({
  origin: productOriginSchema.optional(),
  status: productStatusSchema.optional(),
  sourceState: productSourceStateSchema.optional(),
  /** Part of the name, title, SKU, model or barcode. */
  q: z.string().trim().min(1).max(100).optional(),
  /** Products placed directly in this category. */
  categoryId: categoryFilterSchema.optional(),
  stock: stockFilterSchema.optional(),
  /** Products not shown in the store, for this reason. */
  notShown: notShownReasonSchema.optional(),
});
export type ProductListQuery = z.infer<typeof productListQuerySchema>;

/** The filters the categories of the product list are counted under (no search, no category). */
export const productGroupsQuerySchema = productListQuerySchema.omit({ q: true, categoryId: true });
export type ProductGroupsQuery = z.infer<typeof productGroupsQuerySchema>;

const skuSchema = z
  .string()
  .trim()
  .min(1)
  .max(50)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._/-]*$/, 'letters, digits and . _ / - only');

const factsFields = {
  name: z.string().trim().min(1).max(200),
  sku: skuSchema,
  barcode: optionalText(64),
  model: optionalText(100),
  manufacturer: optionalText(100),
  specs: specListSchema,
};

const factsInputSchema = z.object({
  ...factsFields,
  barcode: factsFields.barcode.default(null),
  model: factsFields.model.default(null),
  manufacturer: factsFields.manufacturer.default(null),
  specs: factsFields.specs.default([]),
});

/** No defaults here: a key that is absent is left as it is. */
const factsChangeSchema = z
  .object({ ...factsFields, extraSpecs: specListSchema })
  .partial()
  .strict();

export const contentInputSchema = z.object({
  title: optionalText(200).optional(),
  description: optionalText(20_000).optional(),
  seoTitle: optionalText(70).optional(),
  seoDescription: optionalText(320).optional(),
});

/**
 * A change of a product's copy and of the lists of its page. A list sent becomes the store's own
 * (adr/0055): send only the lists that changed.
 */
export const productContentChangeSchema = contentInputSchema.extend(
  productListsInputSchema.partial().shape,
);

const priceInputSchema = z
  .object({
    amountMinor: amountMinorSchema.nullable(),
    compareAtMinor: amountMinorSchema.nullable().default(null),
    /** Absent: left as it is. Ignored on a catalog product, whose cost the platform keeps. */
    costMinor: amountMinorSchema.nullable().optional(),
  })
  .refine(
    (price) =>
      price.compareAtMinor === null ||
      (price.amountMinor !== null && price.compareAtMinor > price.amountMinor),
    { message: 'the compare-at price must be higher than the price', path: ['compareAtMinor'] },
  );

const inventoryFields = {
  tracked: z.boolean(),
  quantity: z.number().int().min(0).max(10_000_000),
  lowStockThreshold: z.number().int().min(0).max(10_000_000).nullable(),
};

const inventoryInputSchema = z.object({
  ...inventoryFields,
  quantity: inventoryFields.quantity.default(0),
  lowStockThreshold: inventoryFields.lowStockThreshold.default(null),
});

const categoryIdsSchema = z
  .array(z.uuid())
  .max(20)
  .refine((ids) => new Set(ids).size === ids.length, 'duplicate category');

/** A product the tenant sells that is not in Avrahami's catalog. */
export const createCustomProductRequestSchema = z.object({
  facts: factsInputSchema,
  content: contentInputSchema.default({}),
  price: priceInputSchema.default({ amountMinor: null, compareAtMinor: null }),
  inventory: inventoryInputSchema.optional(),
  categoryIds: categoryIdsSchema.default([]),
  status: z.enum(['DRAFT', 'ACTIVE', 'HIDDEN']).default('DRAFT'),
});
export type CreateCustomProductRequest = z.infer<typeof createCustomProductRequestSchema>;

/**
 * A partial change, guarded by `version` (optimistic lock). Keys that are absent stay as they
 * are; `price` is replaced as a whole. On catalog products `facts` may carry `extraSpecs` only;
 * the copied facts are read-only.
 */
export const updateProductRequestSchema = z.object({
  version: z.number().int().min(1),
  facts: factsChangeSchema.optional(),
  content: productContentChangeSchema.optional(),
  price: priceInputSchema.optional(),
  inventory: z.object(inventoryFields).partial().strict().optional(),
  categoryIds: categoryIdsSchema.optional(),
  status: productStatusSchema.optional(),
});
export type UpdateProductRequest = z.infer<typeof updateProductRequestSchema>;

/**
 * Gives one list of a catalog product back to the engine (adr/0055): it is no longer the store's
 * own, and the engine writes it again from the catalog. Guarded by `version`.
 */
export const restoreProductListRequestSchema = z.object({
  version: z.number().int().min(1),
  list: productTextListSchema,
});
export type RestoreProductListRequest = z.infer<typeof restoreProductListRequestSchema>;

export const MAX_PRODUCT_IMAGES = 20;

/**
 * The product's whole gallery, in order. Existing images are kept by their id (images left out
 * are removed); uploads (`/v1/tenant/media`) are added by their media id.
 */
export const replaceProductMediaRequestSchema = z.object({
  version: z.number().int().min(1),
  items: z
    .array(
      z.discriminatedUnion('kind', [
        z.object({
          kind: z.literal('EXISTING'),
          id: z.uuid(),
          altText: z.string().trim().min(1).max(200).nullable(),
        }),
        z.object({
          kind: z.literal('UPLOAD'),
          mediaId: z.uuid(),
          altText: z.string().trim().min(1).max(200).nullable(),
        }),
      ]),
    )
    .max(MAX_PRODUCT_IMAGES),
});
export type ReplaceProductMediaRequest = z.infer<typeof replaceProductMediaRequestSchema>;

/**
 * The product's manufacturer logo (adr/0041): back to the catalog's, one of the store's uploads
 * (`/v1/tenant/media`) by its media id, or none. Saved on its own, guarded by `version`.
 */
export const setProductLogoRequestSchema = z.object({
  version: z.number().int().min(1),
  logo: z.discriminatedUnion('source', [
    z.object({ source: z.literal('CATALOG') }),
    z.object({ source: z.literal('UPLOAD'), mediaId: z.uuid() }),
    z.object({ source: z.literal('NONE') }),
  ]),
});
export type SetProductLogoRequest = z.infer<typeof setProductLogoRequestSchema>;

/** An image file of the store, served at `/media/<file>` on the store's own host. */
export const MEDIA_FILE_PATTERN = /^[0-9a-f]{64}\.(?:jpg|png|gif|webp|avif)$/;

/**
 * An image the store uploaded. `file` is its name under `/v1/tenant/media/<file>` (tenant admin)
 * and `/media/<file>` (the store's own host).
 */
export const tenantMediaSchema = z.object({
  id: z.uuid(),
  file: z.string().regex(MEDIA_FILE_PATTERN),
  contentType: z.string(),
  byteSize: z.number().int().min(1),
  createdAt: z.iso.datetime(),
});
export type TenantMedia = z.infer<typeof tenantMediaSchema>;

export const categorySchema = z.object({
  id: z.uuid(),
  parentId: z.uuid().nullable(),
  name: z.string(),
  position: z.number().int(),
  isVisible: z.boolean(),
  /** The picture the store chose for the category; without one the store shows a product's. */
  image: tenantMediaSchema.nullable(),
  /** The catalog's picture, under the same media path; shown when `image` is null (adr/0049). */
  catalogFile: z.string().regex(MEDIA_FILE_PATTERN).nullable(),
  origin: productOriginSchema,
  version: z.number().int(),
});
export type Category = z.infer<typeof categorySchema>;

/**
 * Where a product may be placed: only in a sub-category, that is a category that sits under
 * another one and has none below it. A product is never placed in a main category directly, so
 * every list shows category, then sub-category, then products. The API and the admin share this.
 */
export function placeableCategoryIds(
  categories: readonly { id: string; parentId: string | null }[],
): Set<string> {
  const ids = new Set(categories.map((category) => category.id));
  const parents = new Set(categories.map((category) => category.parentId));
  return new Set(
    categories
      .filter(
        (category) =>
          // A category whose parent is gone shows as a main category, so it counts as one.
          category.parentId !== null && ids.has(category.parentId) && !parents.has(category.id),
      )
      .map((category) => category.id),
  );
}

export const createCategoryRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  parentId: z.uuid().nullable().default(null),
  position: z.number().int().min(0).max(100_000).default(0),
  isVisible: z.boolean().default(true),
  mediaId: z.uuid().nullable().default(null),
});
export type CreateCategoryRequest = z.infer<typeof createCategoryRequestSchema>;

export const updateCategoryRequestSchema = z.object({
  version: z.number().int().min(1),
  name: z.string().trim().min(1).max(120).optional(),
  parentId: z.uuid().nullable().optional(),
  position: z.number().int().min(0).max(100_000).optional(),
  isVisible: z.boolean().optional(),
  mediaId: z.uuid().nullable().optional(),
});
export type UpdateCategoryRequest = z.infer<typeof updateCategoryRequestSchema>;

// ---------------------------------------------------------------------------------------------
// Master catalog (platform surface)
// ---------------------------------------------------------------------------------------------

export const catalogIngestCountsSchema = z.object({
  productsSeen: z.number().int(),
  productsCreated: z.number().int(),
  productsUpdated: z.number().int(),
  productsDiscontinued: z.number().int(),
  productsRejected: z.number().int(),
  categoriesSeen: z.number().int(),
  categoriesCreated: z.number().int(),
  categoriesUpdated: z.number().int(),
  categoriesDiscontinued: z.number().int(),
  mediaCopied: z.number().int(),
  mediaFailed: z.number().int(),
});
export type CatalogIngestCounts = z.infer<typeof catalogIngestCountsSchema>;

/** Why an ingest run failed; the message is ours, never text from the feed or the network. */
export const CATALOG_INGEST_ERRORS = [
  'FEED_UNREACHABLE',
  'FEED_REJECTED',
  'FEED_INVALID',
  'ABANDONED',
  'INTERNAL',
] as const;
export const catalogIngestErrorSchema = z.enum(CATALOG_INGEST_ERRORS);
export type CatalogIngestError = z.infer<typeof catalogIngestErrorSchema>;

export const catalogIngestRunSchema = z.object({
  id: z.uuid(),
  mode: catalogIngestModeSchema,
  status: catalogIngestStatusSchema,
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  feedGeneratedAt: z.iso.datetime().nullable(),
  counts: catalogIngestCountsSchema,
  errorCode: catalogIngestErrorSchema.nullable(),
});
export type CatalogIngestRun = z.infer<typeof catalogIngestRunSchema>;

export const catalogSummarySchema = z.object({
  products: z.object({
    active: z.number().int(),
    discontinued: z.number().int(),
    withoutImage: z.number().int(),
    /** Active products the source lists without a price: a store's copy starts unpriced. */
    withoutPrice: z.number().int(),
    bundles: z.number().int(),
  }),
  categories: z.object({ active: z.number().int(), discontinued: z.number().int() }),
  mediaFiles: z.number().int(),
  lastSucceededAt: z.iso.datetime().nullable(),
});
export type CatalogSummary = z.infer<typeof catalogSummarySchema>;

export const catalogIngestRequestSchema = z.object({
  mode: catalogIngestModeSchema.default('INCREMENTAL'),
});
export type CatalogIngestRequest = z.infer<typeof catalogIngestRequestSchema>;

/** The payload of a catalog ingest job (API or schedule → worker). */
export const catalogIngestJobSchema = z.object({
  mode: catalogIngestModeSchema,
  requestedBy: z.uuid().nullable(),
});
export type CatalogIngestJob = z.infer<typeof catalogIngestJobSchema>;

/** `queued: false` when a run is already waiting: duplicate requests are ignored. */
export const catalogIngestRequestedSchema = z.object({ queued: z.boolean() });

// ---------------------------------------------------------------------------------------------
// Platform pickers over the master catalog (`platform.catalog.read`): facts only
// ---------------------------------------------------------------------------------------------

export const masterProductSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  sku: z.string(),
  model: z.string().nullable(),
  categories: z.array(z.string()),
});
export type MasterProduct = z.infer<typeof masterProductSchema>;

export const masterProductListQuerySchema = z.object({
  /** Part of the name, SKU, model or barcode. */
  q: z.string().trim().min(1).max(100).optional(),
  /** Products placed directly in this category. */
  categoryId: categoryFilterSchema.optional(),
});
export type MasterProductListQuery = z.infer<typeof masterProductListQuerySchema>;

/** One active product of the catalog in the short list of all of them. */
export const masterProductIndexItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  sku: z.string(),
  model: z.string().nullable(),
  /** The active categories of the catalog the product is placed in. */
  categoryIds: z.array(z.uuid()),
});
export type MasterProductIndexItem = z.infer<typeof masterProductIndexItemSchema>;

export const masterProductIndexSchema = z.object({ items: z.array(masterProductIndexItemSchema) });
export type MasterProductIndex = z.infer<typeof masterProductIndexSchema>;

/** A category of the catalog, with how many active products are placed directly in it. */
export const masterCategorySchema = productCategoryGroupSchema;
export type MasterCategory = z.infer<typeof masterCategorySchema>;

export const masterCategoryListSchema = productCategoryGroupsSchema;
